'use client';

/**
 * `useSellTrade` — SDK-owned sell mutation. Reads host bindings from
 * `<TradingHostProvider>` via `useTradingHostContext()`.
 */
import { BN } from '@coral-xyz/anchor';
import { useRef } from 'react';
import { createTradeConfirm, isAmbiguousLandingError, LANDING_GRACE_MS } from '../../../tx-engine/watch/trade-confirm.js';
import { computeCheckedSellPositionAccounting } from './helpers/pnl.js';
import { reconcileThesisReplyEligibility } from './helpers/reconcile-thesis-reply-eligibility.js';
import { reconcileSettledTradeEntities } from './helpers/reconcile-trade-entities.js';
import { trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { showCancelledToast, showFailureToast, showSubmittingToast, showSuccessToast } from './helpers/toasts.js';
import { nextTxNumber } from './helpers/trace.js';
import { useTradingHostContext } from './host-context.js';
import { useTradeMutation } from './use-trade-mutation.js';
export function useSellTrade() {
  const deps = useTradingHostContext();
  const abortControllerRef = useRef(null);
  const mutation = useTradeMutation(deps, 'sell', {
    mutationFn: async (params, traceId, deps_0) => {
      if (params.signingDeps === undefined) {
        throw new Error('signingDeps is required for sell transactions');
      }
      const signingDeps = params.signingDeps;
      // Capture before submission: NATS/REST may publish the trade before success fires.
      const updatePosition = deps_0.optimisticPositions?.prepare?.() ?? deps_0.optimisticPositions?.upsert;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const startedAt = Date.now();
      const txNum = nextTxNumber('sell');
      const grossQuoteAmountD6 = params.grossQuoteAmountD6.toString();
      if (!/^[1-9]\d*$/.test(grossQuoteAmountD6)) {
        deps_0.logger.error('trade.sell_intent.invalid', {
          traceId,
          reason: 'invalid_gross_quote_d6'
        });
        throw new Error('grossQuoteAmountD6 must be a positive D6 integer');
      }
      const formattedAmount = deps_0.formatBalanceD6(grossQuoteAmountD6);
      deps_0.txSubmission.startSubmission(traceId, 'sell');
      showSubmittingToast(deps_0, traceId, 'sell', txNum, formattedAmount, 1, 3, abortController);
      // NATS early-confirm (armed after the build import resolves). Declared
      // here so the catch/finally can reach it.
      let confirm = null;
      // A throw before `submitTx` is a build failure (RPC reads, account
      // loading); the engine never saw the trade, so it must not be reported
      // as an unknown submission error.
      let stage = 'build';
      try {
        const {
          buildAmmSwap,
          buildSell,
          CP_SWAP_PROGRAM_ID
        } = await import('@morfi/contract-sdk');
        const {
          PublicKey
        } = await import('@solana/web3.js');
        deps_0.txSubmission.updateStage(traceId, 'signing');
        const connection = deps_0.getConnection();
        // Graduated markets trade on the cp-swap pool: route the sell through
        // swap_base_input (outcome→USDC) rather than the bonding-curve `sell`.
        const isAmm = params.marketPhase === 'amm';
        const minUsdcOut = new BN(0);
        const sellAccounting = computeCheckedSellPositionAccounting({
          positionAccounting: params.positionAccounting,
          tokenAmountD6: params.tokensNeededD6.toString(),
          netQuoteProceedsD6: params.expectedNetUsdcOutD6.toString(),
          isAmm
        });
        const sellRealizedPnlD6 = sellAccounting.available ? sellAccounting.value.realizedPnlDeltaD6.toString() : null;
        // User intent over on-chain net-out — same reasoning as `useBuyTrade`.
        const sellShareCtx = {
          amountD6: grossQuoteAmountD6,
          shareAmountD6: params.tokensNeededD6.toString(),
          outcomeIndex: params.outcomeIndex
        };
        const fireSellSuccess = signature => {
          const sellCtx = {
            traceId,
            instruction: 'sell',
            walletType: params.activeWallet.type,
            walletAddress: params.activeWallet.address,
            marketAddress: params.marketAddress,
            outcomeIndex: params.outcomeIndex,
            latencyMs: Date.now() - startedAt,
            // Revenue attribution: notional = gross quote output (pre-fee).
            tradeNotionalD6: params.grossQuoteAmountD6.toString(),
            ...(params.platformFeeD6 !== undefined ? {
              platformFeeD6: params.platformFeeD6
            } : {})
          };
          trackTxSuccess(deps_0, sellCtx, signature ?? '');
          deps_0.txSubmission.setSignature(traceId, signature ?? '');
          deps_0.txSubmission.updateStage(traceId, 'confirmed');
          params.onConfirmed?.();
          showSuccessToast(deps_0, traceId, 'sell', txNum, formattedAmount, signature ?? '', params.marketAddress, params.activeWallet.address, sellShareCtx, sellRealizedPnlD6, params.marketTitle,
          // Graduated/AMM (winner) sells don't offer the thesis prompt (MOR-729).
          !isAmm);
          // Optimistically decrement the held balance so the trade form's
          // holding value ticks down instantly (price is already realtime).
          deps_0.optimisticBalance?.patch({
            walletAddress: params.activeWallet.address,
            outcomeMint: params.outcomeMint,
            rawDeltaD6: -BigInt(params.tokensNeededD6.toString())
          });
          // Map the canonical checked sell result into every optimistic
          // positions cache; the host fails closed on stale/malformed rows.
          updatePosition?.({
            walletAddress: params.activeWallet.address,
            market: params.marketAddress,
            outcomeIndex: params.outcomeIndex,
            outcomeMint: params.outcomeMint,
            tokenDeltaD6: -BigInt(params.tokensNeededD6.toString()),
            quoteD6: BigInt(params.expectedNetUsdcOutD6.toString()),
            grossQuoteD6: BigInt(params.feeExcludedQuoteOutputD6.toString()),
            penaltyD6: BigInt(params.sellPenaltyFeeD6?.toString() ?? 0),
            walletDebitD6: null,
            isAmm
          });
        };
        confirm = createTradeConfirm({
          wallet: params.activeWallet.address,
          market: params.marketAddress,
          outcomeIdx: params.outcomeIndex,
          side: 'sell',
          venue: isAmm ? 'amm' : 'bc',
          submitStartMs: startedAt,
          debug: (msg, ctx) => deps_0.logger.info(`[tx] ⚡ ${msg}`, {
            traceId,
            ...ctx
          })
        }, {
          fireSuccess: fireSellSuccess,
          reconcileSignature: signature_0 => {
            deps_0.txSubmission.setSignature(traceId, signature_0);
            if (!isAmm) deps_0.thesis?.reconcileDialogSignature(traceId, signature_0);
            showSuccessToast(deps_0, traceId, 'sell', txNum, formattedAmount, signature_0, params.marketAddress, params.activeWallet.address, sellShareCtx, sellRealizedPnlD6, params.marketTitle, !isAmm);
          }
        });
        const sdkResult = isAmm ? await buildAmmSwap(connection, {
          market: new PublicKey(params.marketAddress),
          outcomeIndex: params.outcomeIndex,
          user: new PublicKey(params.activeWallet.address),
          direction: 'sell',
          amountIn: params.tokensNeededD6,
          minAmountOut: minUsdcOut,
          collateralMint: new PublicKey(deps_0.getUsdcMint()),
          payer: new PublicKey(params.activeWallet.address)
        }) : await buildSell(connection, {
          market: new PublicKey(params.marketAddress),
          outcomeIndex: params.outcomeIndex,
          user: new PublicKey(params.activeWallet.address),
          tokensIn: params.tokensNeededD6,
          minUsdcOut,
          payer: new PublicKey(params.activeWallet.address)
        });
        stage = 'submit';
        const result = await deps_0.submitTx({
          instructions: sdkResult.instructions,
          signers: sdkResult.signerKeys
        }, {
          instructionName: isAmm ? 'swapBaseInput' : 'sell',
          traceId,
          signingDeps,
          activeWallet: params.activeWallet,
          turnkeyDeps: deps_0.turnkeyDeps,
          signal: abortController.signal,
          slippageBps: params.slippageBps,
          suggestedSlippageBps: params.suggestedSlippageBps,
          marketPhase: params.marketPhase,
          ...(isAmm ? {
            programIdHint: CP_SWAP_PROGRAM_ID.toBase58()
          } : {})
        });
        if (result.ok) {
          confirm.resolveFromTurnkey(result.signature);
          return result;
        }
        if (abortController.signal.aborted) {
          if (!confirm.wasFired()) {
            showCancelledToast(deps_0, traceId);
            deps_0.txSubmission.updateStage(traceId, 'cancelled');
          }
          return result;
        }
        // Submit failed. A definitive on-chain revert (TX_CONFIRM_FAILED) is
        // AUTHORITATIVE for THIS trade — it landed and FAILED. Never let a NATS
        // match keep it as success (a match on a reverted trade can only be a
        // stale/replayed event). For ambiguous post-send errors the tx may still
        // have landed — wait briefly for the indexer's trade event (ground truth).
        const isDefinitiveRevert = result.error.code === 'TX_CONFIRM_FAILED';
        let landed = !isDefinitiveRevert && confirm.wasFired();
        if (!landed && !isDefinitiveRevert && isAmbiguousLandingError(result.error.code)) {
          landed = await confirm.awaitLanding(LANDING_GRACE_MS);
        }
        if (landed) {
          return {
            ok: true,
            signature: confirm.firedSignature() ?? '',
            traceId
          };
        }
        // Genuine failure. Release the watch FIRST so a late NATS event cannot
        // fire a success on top of the failure UX.
        confirm.release();
        trackTxFailure(deps_0, {
          traceId,
          instruction: 'sell',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: params.outcomeIndex,
          latencyMs: Date.now() - startedAt
        }, result.error);
        deps_0.txSubmission.setError(traceId, {
          code: result.error.code,
          userMessageKey: result.error.userMessageKey
        });
        showFailureToast(deps_0, traceId, result.error, slippageBps => {
          retryWithSlippage(params, slippageBps);
        });
        return result;
      } catch (err) {
        if (confirm?.wasFired() === true) {
          deps_0.logger.warn('Sell leg threw after NATS-confirmed landing; keeping success', {
            traceId,
            errorClass: err instanceof Error ? 'Error' : typeof err
          });
          return {
            ok: true,
            signature: confirm.firedSignature() ?? '',
            traceId
          };
        }
        if (abortController.signal.aborted) {
          showCancelledToast(deps_0, traceId);
          deps_0.txSubmission.updateStage(traceId, 'cancelled');
          return {
            ok: false,
            error: {
              code: 'TX_CANCELLED',
              stage: 'preflight',
              userMessageKey: 'tx.errors.cancelled',
              traceId,
              recoveryAction: {
                type: 'not_retryable'
              }
            },
            traceId
          };
        }
        const code = stage === 'build' ? 'TX_BUILD_FAILED' : 'TX_UNKNOWN';
        const userMessageKey = stage === 'build' ? 'tx.errors.buildFailed' : 'tx.errors.unknown';
        deps_0.txSubmission.setError(traceId, {
          code,
          userMessageKey
        });
        showFailureToast(deps_0, traceId, {
          code,
          stage: 'preflight',
          userMessageKey,
          traceId,
          recoveryAction: {
            type: 'prompt_retry'
          }
        });
        trackTxException(deps_0, {
          traceId,
          instruction: 'sell',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: params.outcomeIndex,
          latencyMs: Date.now() - startedAt
        }, err, code);
        throw err;
      } finally {
        confirm?.release();
      }
    },
    onSettled: (data, _error, params_0) => {
      const signature_1 = data?.ok === true && data.signature.length > 0 ? data.signature : undefined;
      reconcileSettledTradeEntities(deps, data);
      reconcileThesisReplyEligibility(deps, params_0.marketAddress);
      deps.onMutationSettled('sell', {
        marketAddress: params_0.marketAddress,
        walletAddress: params_0.activeWallet.address,
        ...(signature_1 === undefined ? {} : {
          signature: signature_1
        })
      });
    }
  });
  function retryWithSlippage(params_1, slippageBps_0) {
    mutation.mutate({
      ...params_1,
      slippageBps: slippageBps_0
    });
  }
  return {
    ...mutation,
    abortControllerRef
  };
}